import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError, forkJoin, of } from 'rxjs';
import { catchError, map, concatMap } from 'rxjs/operators';
import { Department, DepartmentPagedResponse } from '../models/Department';
import { ErrorService } from '../shared/services/error.service';
import { environment } from '../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class DepartmentService {
  private readonly baseUrl = environment.microservices?.staff || environment.apiUrl || '/api';

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) { }

  /**
   * Lista departamentos com paginação
   */
  getDepartmentsPaginated(page: number = 1, pageSize: number = 10, filter?: string): Observable<DepartmentPagedResponse> {
    let httpParams = new HttpParams()
      .set('page', page.toString())
      .set('pageSize', pageSize.toString());

    if (filter && filter.trim()) {
      httpParams = httpParams.set('filter', filter.trim());
    }

    const url = `${this.baseUrl}/department/list`;
    
    return this.http.get<DepartmentPagedResponse>(url, { params: httpParams })
      .pipe(
        map(response => this.mapDepartmentResponse(response)),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao buscar departamentos');
          return throwError(() => err);
        })
      );
  }

  /**
   * Lista todos os departamentos para combos (cadastro/edição).
   * Algumas APIs ignoram `pageSize` alto e devolvem só 10 itens; este método percorre todas as páginas.
   */
  getAllDepartments(filter?: string): Observable<Department[]> {
    const pageSize = 100;
    return this.getDepartmentsPaginated(1, pageSize, filter).pipe(
      concatMap(first => {
        const list = [...(first.departments || [])];
        const totalPages = Math.min(
          this.computeDepartmentListTotalPages(first, pageSize),
          500
        );

        if (totalPages <= 1) {
          return of(list);
        }

        const requests: Observable<DepartmentPagedResponse>[] = [];
        for (let p = 2; p <= totalPages; p++) {
          requests.push(this.getDepartmentsPaginated(p, pageSize, filter));
        }

        return forkJoin(requests).pipe(
          map((chunks: DepartmentPagedResponse[]) => {
            const merged = [...list];
            for (const chunk of chunks) {
              merged.push(...(chunk.departments || []));
            }
            return merged;
          })
        );
      })
    );
  }

  /** Deriva quantas páginas existem quando a API não envia `totalPages` ou ignora `pageSize`. */
  private computeDepartmentListTotalPages(res: DepartmentPagedResponse, requestedPageSize: number): number {
    const items = res.departments?.length ?? 0;
    const tcRaw =
      res.totalCount ??
      (res as any).TotalCount ??
      items;
    const tc = typeof tcRaw === 'number' ? tcRaw : Number(tcRaw) || items;

    let tp = res.totalPages ?? (res as any).TotalPages ?? 0;
    // API às vezes responde totalPages=1 mesmo havendo mais registros (pageSize ignorado).
    if (tp === 1 && tc > items && items > 0) {
      tp = 0;
    }

    if (tp > 0) {
      return tp;
    }

    if (tc <= 0 || items <= 0) {
      return 1;
    }
    const denom = Math.min(items, requestedPageSize);
    return Math.max(1, Math.ceil(tc / denom));
  }

  /**
   * Obtém os detalhes de um departamento (view)
   */
  getDepartmentById(departmentId: number): Observable<Department> {
    const url = `${this.baseUrl}/department/view/${departmentId}`;

    return this.http.get<any>(url)
      .pipe(
        map(response => {
          // API retorna { success, department, timestamp }
          const dto = response?.department ?? response;
          return this.mapDepartmentFromDto(dto);
        }),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao buscar departamento');
          return throwError(() => err);
        })
      );
  }

  /**
   * Mapeia a resposta da API para o formato esperado
   */
  private mapDepartmentResponse(response: any): DepartmentPagedResponse {
    return {
      success: response.success ?? response.Success ?? false,
      page: response.page ?? response.Page ?? 1,
      pageSize: response.pageSize ?? response.PageSize ?? 10,
      totalCount: response.totalCount ?? response.TotalCount ?? 0,
      totalPages: response.totalPages ?? response.TotalPages ?? 0,
      departments: (
        response.departments ??
        response.Departments ??
        response.items ??
        response.Items ??
        []
      ).map((dto: any) => this.mapDepartmentFromDto(dto)),
      timestamp: response.timestamp || new Date().toISOString()
    };
  }

  /**
   * Cria um novo departamento
   */
  createDepartment(departmentData: {
    name: string;
    description?: string | null;
    isActive?: boolean;
  }): Observable<Department> {
    const url = `${this.baseUrl}/department/create`;
    
    // Payload simples em camelCase
    const payload = {
      name: departmentData.name,
      description: departmentData.description || null,
      isActive: departmentData.isActive !== undefined ? departmentData.isActive : true
    };
    
    return this.http.post<any>(url, payload)
      .pipe(
        map(response => this.mapDepartmentFromDto(response)),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao criar departamento');
          return throwError(() => err);
        })
      );
  }

  /**
   * Atualiza um departamento (principalmente para atribuir gerente)
   */
  updateDepartment(departmentId: number, departmentData: {
    name: string;
    description?: string | null;
    managerStaffId?: number | null;
    isActive?: boolean;
  }): Observable<Department> {
    const url = `${this.baseUrl}/department/update/${departmentId}`;
    
    // Garantir que managerStaffId seja número ou null (nunca string)
    let managerStaffId: number | null = null;
    if (departmentData.managerStaffId !== null && departmentData.managerStaffId !== undefined) {
      if (typeof departmentData.managerStaffId === 'number') {
        managerStaffId = departmentData.managerStaffId;
      } else {
        const parsed = parseInt(String(departmentData.managerStaffId), 10);
        managerStaffId = isNaN(parsed) ? null : parsed;
      }
    }
    
    // Payload em camelCase
    const payload = {
      name: departmentData.name,
      description: departmentData.description || null,
      managerStaffId: managerStaffId,
      isActive: departmentData.isActive !== undefined ? departmentData.isActive : true
    };
    
    return this.http.put<any>(url, payload)
      .pipe(
        map(response => this.mapDepartmentFromDto(response)),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao atualizar departamento');
          return throwError(() => err);
        })
      );
  }

  /**
   * Exclui um departamento
   */
  deleteDepartment(departmentId: number): Observable<void> {
    const url = `${this.baseUrl}/department/delete/${departmentId}`;

    return this.http.delete<any>(url)
      .pipe(
        map(() => void 0),
        catchError(err => {
          this.errorService.showErrorMessage('Erro ao excluir departamento');
          return throwError(() => err);
        })
      );
  }

  /**
   * Mapeia DTO do backend para interface Department
   * O backend retorna campos em PascalCase (Name, Description, IsActive, etc.)
   */
  private mapDepartmentFromDto(dto: any): Department {
    return {
      id: dto.Id ?? dto.id ?? 0,
      name: dto.Name ?? dto.name ?? '',
      description: dto.Description ?? dto.description ?? null,
      isActive: dto.IsActive ?? dto.isActive ?? true,
      managerStaffId: dto.ManagerStaffId ?? dto.managerStaffId ?? null,
      managerStaffName: dto.ManagerStaffName ?? dto.managerStaffName ?? null,
      managerStaffRegistration: dto.ManagerStaffRegistration ?? dto.managerStaffRegistration ?? null,
      createdAt: dto.CreatedAt ?? dto.createdAt ?? '',
      updatedAt: dto.UpdatedAt ?? dto.updatedAt ?? null,
      createdBy: dto.CreatedBy ?? dto.createdBy ?? null,
      updatedBy: dto.UpdatedBy ?? dto.updatedBy ?? null
    };
  }
}

